// Icônes d'objets : chaque objet est rendu en 3D une fois au chargement (même éclairage, même angle pour tous),
// puis gardé en image. Armes : modèles Quaternius (CC0) ; autres objets : petits modèles construits ici.
import * as THREE from 'three';
import { ITEMS } from '../shared/items';
import { WEAPON_MODELS, buildWeapon, modObject, weaponObject } from './weaponModels';
import { modsKey, type Mods, type WeaponId } from '../shared/weapons';
import { buildHelmet, buildVest } from './gear';

const PX = 96; // pixels par case
const icons = new Map<string, string>();
export const iconOf = (id: string) => icons.get(id) ?? '';

const mat = (color: number, metal = 0, rough = 0.6) => new THREE.MeshStandardMaterial({ color, metalness: metal, roughness: rough });
const brass = mat(0xc9a14a, 0.7, 0.35), copper = mat(0xb5653a, 0.6, 0.4), steel = mat(0x8d9196, 0.7, 0.35), dark = mat(0x26292d, 0.2, 0.7);

function mesh(geo: THREE.BufferGeometry, m: THREE.Material, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0) {
  const o = new THREE.Mesh(geo, m);
  o.position.set(x, y, z);
  o.rotation.set(rx, ry, rz);
  return o;
}

// Boîte de cartouches : carton coloré, bande, quelques cartouches posées dessus
function ammoBox(color: number, shell: boolean, ap = false) {
  const g = new THREE.Group();
  g.add(mesh(new THREE.BoxGeometry(1, 0.55, 0.7), mat(color, 0, 0.8)));
  g.add(mesh(new THREE.BoxGeometry(1.01, 0.14, 0.71), mat(0xe9dfc4, 0, 0.8), 0, 0.08, 0));
  for (let i = 0; i < 3; i++) {
    const c = new THREE.Group();
    c.add(mesh(new THREE.CylinderGeometry(0.07, 0.07, shell ? 0.42 : 0.36, 12), shell ? mat(0xa0302a, 0, 0.5) : brass));
    c.add(mesh(new THREE.CylinderGeometry(0.075, 0.075, 0.1, 12), brass, 0, shell ? -0.2 : -0.16, 0));
    if (!shell) c.add(mesh(new THREE.ConeGeometry(0.07, 0.14, 12), ap ? dark : copper, 0, 0.25, 0));
    c.position.set(-0.25 + i * 0.25, 0.42, 0.05);
    c.rotation.z = Math.PI / 2;
    c.rotation.y = 0.2 * (i - 1);
    g.add(c);
  }
  return g;
}


// Sac à dos : corps, rabat, poche frontale, bretelles ; plus il est grand, plus il a de poches
function backpack(color: number, w: number, h: number, pockets: number) {
  const g = new THREE.Group();
  const dark = new THREE.Color(color).multiplyScalar(0.75).getHex();
  g.add(mesh(new THREE.BoxGeometry(w, h, 0.5), mat(color, 0, 0.9)));
  g.add(mesh(new THREE.BoxGeometry(w * 1.02, h * 0.28, 0.54), mat(dark, 0, 0.9), 0, h * 0.38, 0.01));
  g.add(mesh(new THREE.BoxGeometry(w * 0.7, h * 0.36, 0.2), mat(dark, 0, 0.9), 0, -h * 0.18, 0.32));
  for (let i = 0; i < pockets; i++) g.add(mesh(new THREE.BoxGeometry(0.16, h * 0.4, 0.4), mat(dark, 0, 0.9), (i % 2 ? 1 : -1) * (w / 2 + 0.08), -h * 0.1 + Math.floor(i / 2) * h * 0.3, 0));
  g.add(mesh(new THREE.BoxGeometry(w * 0.5, 0.05, 0.05), mat(0x222222), 0, h * 0.38, 0.29));
  return g;
}

const BUILD: Record<string, () => THREE.Object3D> = {
  // Registre de la mairie : gros livre relié, tranche claire, étiquette
  q_ledger: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(0.9, 1.25, 0.28), mat(0x6b2f25, 0, 0.8)));
    g.add(mesh(new THREE.BoxGeometry(0.84, 1.18, 0.29), mat(0xe8dfc8, 0, 0.9), 0.04, 0, 0));
    g.add(mesh(new THREE.BoxGeometry(0.5, 0.3, 0.02), mat(0xd9c99a, 0, 0.7), 0, 0.2, 0.15));
    g.add(mesh(new THREE.BoxGeometry(0.08, 1.26, 0.3), mat(0x4e2119, 0, 0.8), -0.43, 0, 0));
    return g;
  },
  b_small: () => backpack(0x6b6248, 0.9, 0.9, 0),
  b_patrol: () => backpack(0x5a6339, 1.0, 1.15, 2),
  b_assault: () => backpack(0x4d5536, 1.0, 1.35, 2),
  b_raid: () => backpack(0x3f4532, 1.2, 1.4, 4),
  // Casques et gilets : le modèle porté par le personnage (gear.ts)
  h_hard: () => buildHelmet('h_hard'),
  h_mil: () => buildHelmet('h_mil'),
  v_soft: () => buildVest('v_soft'),
  v_police: () => buildVest('v_police'),
  v_mil: () => buildVest('v_mil'),
  m_splint: () => {
    const g = new THREE.Group();
    for (const x of [-0.2, 0.2]) g.add(mesh(new THREE.BoxGeometry(0.12, 1.4, 0.06), mat(0xb9b2a3), x, 0, 0));
    for (const y of [-0.4, 0, 0.4]) g.add(mesh(new THREE.BoxGeometry(0.6, 0.1, 0.1), mat(0x3a6fb0), 0, y, 0));
    return g;
  },
  a_556ap: () => ammoBox(0x5d6b3c, false, true),
  a_762ap: () => ammoBox(0x6b4e2e, false, true),
  a_9ap: () => ammoBox(0x3f5260, false, true),
  a_556: () => ammoBox(0x5d6b3c, false),
  a_762: () => ammoBox(0x6b4e2e, false),
  a_9: () => ammoBox(0x3f5260, false),
  a_12: () => ammoBox(0x7a2a24, true),
  m_bandage: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.6, 24), mat(0xf1ece2, 0, 0.9), 0, 0, 0, 0, 0, Math.PI / 2));
    g.add(mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.62, 16), mat(0xbfb7a8), 0, 0, 0, 0, 0, Math.PI / 2));
    g.add(mesh(new THREE.BoxGeometry(0.5, 0.02, 0.4), mat(0xf1ece2, 0, 0.9), 0.15, -0.45, 0.3));
    return g;
  },
  m_kit: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(1.6, 0.8, 0.6), mat(0xb3322b, 0, 0.6)));
    g.add(mesh(new THREE.BoxGeometry(0.5, 0.14, 0.62), mat(0xf4f1ea), 0, 0, 0.01));
    g.add(mesh(new THREE.BoxGeometry(0.14, 0.5, 0.62), mat(0xf4f1ea), 0, 0, 0.01));
    g.add(mesh(new THREE.TorusGeometry(0.22, 0.05, 8, 16, Math.PI), dark, 0, 0.42, 0));
    return g;
  },
  l_watch: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.TorusGeometry(0.42, 0.1, 10, 28), mat(0x5a3b26, 0, 0.8), 0, 0, 0, 0.5, 0, 0));
    g.add(mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.12, 24), steel, 0, 0.1, 0.35, Math.PI / 2 - 0.5, 0, 0));
    g.add(mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.13, 24), mat(0xf2efe6), 0, 0.1, 0.36, Math.PI / 2 - 0.5, 0, 0));
    return g;
  },
  l_drive: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(0.8, 0.14, 1.05), mat(0x3a3e44, 0.5, 0.4)));
    g.add(mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.02, 28), steel, 0, 0.08, -0.1));
    g.add(mesh(new THREE.BoxGeometry(0.5, 0.02, 0.2), mat(0xe8e2d4), 0, 0.08, 0.35));
    return g;
  },
  l_board: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(1, 0.05, 0.75), mat(0x2f6b3a, 0, 0.5)));
    for (const [x, z, w] of [[-0.2, -0.1, 0.3], [0.25, 0.15, 0.2], [0.2, -0.2, 0.15], [-0.25, 0.22, 0.18]] as const) g.add(mesh(new THREE.BoxGeometry(w, 0.06, w), dark, x, 0.05, z));
    for (let i = 0; i < 8; i++) g.add(mesh(new THREE.BoxGeometry(0.06, 0.02, 0.1), brass, -0.42 + i * 0.12, 0.03, 0.37));
    return g;
  },
  l_meds: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.7, 20), mat(0xd9832e, 0, 0.3)));
    g.add(mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.18, 20), mat(0xf4f1ea), 0, 0.42, 0));
    g.add(mesh(new THREE.CylinderGeometry(0.305, 0.305, 0.32, 20), mat(0xf4f1ea, 0, 0.9), 0, -0.05, 0));
    return g;
  },
  l_cable: () => {
    const g = new THREE.Group();
    for (let i = 0; i < 4; i++) g.add(mesh(new THREE.TorusGeometry(0.38, 0.05, 8, 28), copper, 0, i * 0.1 - 0.15, 0, Math.PI / 2 + 0.25, 0, i * 0.15));
    g.scale.set(1, 1.8, 1);
    return g;
  },
  l_binoc: () => {
    const g = new THREE.Group();
    for (const x of [-0.26, 0.26]) {
      g.add(mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.7, 18), dark, x, 0, 0, Math.PI / 2, 0, 0));
      g.add(mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.12, 18), mat(0x44484d, 0.4, 0.4), x, 0, 0.35, Math.PI / 2, 0, 0));
    }
    g.add(mesh(new THREE.BoxGeometry(0.3, 0.12, 0.3), dark));
    return g;
  },
  l_radio: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(0.55, 1.1, 0.3), mat(0x2f3338, 0.2, 0.6)));
    g.add(mesh(new THREE.BoxGeometry(0.4, 0.3, 0.02), mat(0x6f7a5e), 0, 0.25, 0.16));
    for (let i = 0; i < 4; i++) g.add(mesh(new THREE.BoxGeometry(0.36, 0.03, 0.02), mat(0x15171a), 0, -0.1 - i * 0.08, 0.16));
    g.add(mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.7, 8), steel, 0.18, 0.9, 0));
    return g;
  },
  l_fuel: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(0.75, 1, 0.42), mat(0xa52f26, 0.2, 0.5)));
    g.add(mesh(new THREE.BoxGeometry(0.45, 0.1, 0.12), mat(0x8a2620), -0.05, 0.56, 0));
    g.add(mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.2, 12), mat(0x2a2a2a), 0.28, 0.58, 0, 0, 0, -0.5));
    return g;
  },
  l_toolbox: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(1.5, 0.6, 0.6), mat(0xb7342a, 0.3, 0.5)));
    g.add(mesh(new THREE.BoxGeometry(1.52, 0.12, 0.62), mat(0x707377, 0.6, 0.4), 0, 0.3, 0));
    g.add(mesh(new THREE.TorusGeometry(0.28, 0.05, 8, 16, Math.PI), dark, 0, 0.36, 0));
    return g;
  },
  l_cigs: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(0.62, 0.95, 0.26), mat(0xe8e4da, 0, 0.6)));
    g.add(mesh(new THREE.BoxGeometry(0.63, 0.3, 0.27), mat(0xb7342a, 0, 0.5), 0, 0.32, 0));
    for (const x of [-0.12, 0, 0.12]) g.add(mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.16, 8), mat(0xd8b080, 0, 0.7), x, 0.55, 0));
    return g;
  },
  l_cash: () => {
    const g = new THREE.Group();
    for (let i = 0; i < 4; i++) g.add(mesh(new THREE.BoxGeometry(1.1, 0.07, 0.55), mat(i % 2 ? 0x6f8f5a : 0x7fa067, 0, 0.8), (i % 2) * 0.04, i * 0.075, 0, 0, i * 0.05));
    g.add(mesh(new THREE.BoxGeometry(0.22, 0.32, 0.57), mat(0xe8e4da, 0, 0.7), 0, 0.12, 0));
    return g;
  },
  l_ration: () => {
    const g = new THREE.Group();
    g.add(mesh(new THREE.BoxGeometry(0.9, 0.95, 0.22), mat(0x6b7a44, 0, 0.9)));
    g.add(mesh(new THREE.BoxGeometry(0.5, 0.18, 0.23), mat(0x1d1f21, 0, 0.8), 0, 0.15, 0));
    g.add(mesh(new THREE.BoxGeometry(0.9, 0.08, 0.24), mat(0x55603a, 0, 0.9), 0, 0.46, 0));
    return g;
  },
  l_tags: () => {
    const g = new THREE.Group(), steel2 = mat(0xb0b4b8, 0.8, 0.3);
    g.add(mesh(new THREE.BoxGeometry(0.5, 0.8, 0.04), steel2, -0.12, 0, 0, 0, 0, 0.15));
    g.add(mesh(new THREE.BoxGeometry(0.5, 0.8, 0.04), steel2, 0.18, -0.08, 0.05, 0, 0, -0.2));
    for (let i = 0; i < 10; i++) g.add(mesh(new THREE.SphereGeometry(0.025, 6, 4), steel2, -0.3 + i * 0.07, 0.48 + Math.sin(i / 3) * 0.08, 0));
    return g;
  },
  l_gold: () => {
    const g = new THREE.Group();
    const gold = mat(0xe0b44a, 0.9, 0.25);
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 1.6 + 0.6;
      g.add(mesh(new THREE.TorusGeometry(0.09, 0.03, 8, 14), gold, Math.cos(a) * 0.38, Math.sin(a) * 0.3, 0, 0, i % 2 ? Math.PI / 2 : 0, a));
    }
    g.add(mesh(new THREE.OctahedronGeometry(0.12), mat(0x3d7fb8, 0.3, 0.1), 0, -0.36, 0));
    return g;
  },
};

// Clé (anneau, tige, panneton) ou carte d'accès magnétique ; une couleur par serrure
const KEY_COLORS: Record<string, number> = { k_superette: 0xc9a14a, k_pharma: 0x7fb7a0, k_camp: 0x8a9a5a, k_base: 0xb87333, k_bunker: 0xc43b30 };
function keyIcon(id: string) {
  const g = new THREE.Group(), m = mat(KEY_COLORS[id] ?? 0xc9a14a, 0.8, 0.3);
  if (id === 'k_bunker') {
    g.add(mesh(new THREE.BoxGeometry(1.1, 0.7, 0.04), mat(0xe8e4da, 0.1, 0.5)));
    g.add(mesh(new THREE.BoxGeometry(1.1, 0.16, 0.05), m, 0, 0.18, 0));
    g.add(mesh(new THREE.BoxGeometry(0.22, 0.18, 0.05), mat(0xd9a52a, 0.8, 0.3), -0.3, -0.12, 0));
    return g;
  }
  g.add(mesh(new THREE.TorusGeometry(0.22, 0.07, 8, 18), m, -0.45, 0, 0));
  g.add(mesh(new THREE.BoxGeometry(0.75, 0.1, 0.06), m, 0.12, 0, 0));
  for (const [x, h] of [[0.32, 0.18], [0.42, 0.12], [0.48, 0.2]] as const) g.add(mesh(new THREE.BoxGeometry(0.07, h, 0.06), m, x, -h / 2, 0));
  g.add(mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.04, 10).rotateX(Math.PI / 2), mat(0x2a2c2e), -0.45, 0.28, 0.03));
  return g;
}

// Rendu des icônes : une scène et un moteur gardés pour dessiner à la demande les armes équipées d'accessoires
let renderer: THREE.WebGLRenderer | null = null;
const scene = new THREE.Scene();

function snap(obj: THREE.Object3D, w: number, h: number, profile: boolean) {
  const holder = new THREE.Group();
  holder.add(obj);
  if (!profile) holder.rotation.set(0.45, -0.6, 0); // vue de trois quarts
  scene.add(holder);
  // Cadrage : l'objet remplit la case (en gardant ses proportions)
  const box = new THREE.Box3().setFromObject(holder);
  const size = box.getSize(new THREE.Vector3()), c = box.getCenter(new THREE.Vector3());
  const wpx = w * PX, hpx = h * PX, aspect = wpx / hpx;
  const half = Math.max(size.x / aspect, size.y) * 0.56;
  const cam = new THREE.OrthographicCamera(-half * aspect, half * aspect, half, -half, -10, 10);
  cam.position.set(c.x, c.y, c.z + 5);
  cam.lookAt(c);
  renderer!.setSize(wpx, hpx, false);
  renderer!.setClearColor(0x000000, 0);
  renderer!.render(scene, cam);
  scene.remove(holder);
  return renderer!.domElement.toDataURL('image/png');
}

// Arme vue de profil, canon vers la droite
function weaponIcon(id: WeaponId, mods?: Mods) {
  const o = new THREE.Group().add(weaponObject(buildWeapon(id, mods), WEAPON_MODELS[id]));
  o.rotation.set(0, Math.PI / 2, 0);
  return o;
}

// Icône d'un objet précis : une arme équipée d'accessoires a la sienne (dessinée la première fois qu'on la demande)
export function iconFor(it: { id: string; mods?: Mods }) {
  const d = ITEMS[it.id];
  const key = d?.weapon && modsKey(it.mods) ? `${it.id}|${modsKey(it.mods)}` : it.id;
  if (key === it.id || !renderer || !d?.weapon) return icons.get(key) ?? iconOf(it.id);
  if (!icons.has(key)) icons.set(key, snap(weaponIcon(d.weapon, it.mods), d.w, d.h, true));
  return icons.get(key)!;
}

export async function initIcons() {
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  scene.add(new THREE.HemisphereLight(0xffffff, 0x404040, 1.4));
  const key = new THREE.DirectionalLight(0xffffff, 2.2);
  key.position.set(2, 4, 3);
  scene.add(key);
  for (const def of Object.values(ITEMS)) {
    let obj: THREE.Object3D | undefined;
    if (def.weapon) obj = weaponIcon(def.weapon);
    else if (def.mod) { obj = modObject(def.mod); obj.rotation.set(0.25, -0.35, 0); }
    else if (def.key) obj = keyIcon(def.id);
    else obj = BUILD[def.id]?.();
    if (!obj) continue;
    icons.set(def.id, snap(obj, def.w, def.h, !!(def.weapon || def.mod)));
  }
}
